/**
 * Membership application
 * @see {@link ../components/Common/UserProfileForm.jsx}
 * @see {@link @smfa/utils/src/common/products.js}
 */
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { get, has, isFunction, isString, size, trim } from "lodash";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Button,
  Alert,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Container,
  Divider,
} from "@mui/material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";
import aboutimg from "../styles/img/appointment-bg4.jpg";
import Preloader from "../components/Common/Preloader";
import {
  updateNullOfObjectValues,
  isEmail,
  objectToQueryString,
  generateRandomString,
} from "../utils/helper";
import { storeCheckoutToken } from "../utils/paymentSession";
import { onlineApplicationSubmit } from "../actions/pagecontent.actions";
import { createPaymentIntent } from "../actions/payment.actions";
//import PageContentLoader from "./Common/PageContentLoader";
import { getIpAddress } from "@smfa/utils";
import { products } from "@smfa/utils/src/common/products";
import {
  getSuggestedLMICSelection,
  validateLMICSelection,
} from "@smfa/utils/src/common/lmic_countries";
import StripePaymentModal, {
  PaymentIntentLoader,
  processStripePaymentIntent,
} from "../components/Common/StripePayment";

// Import reusable components
import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";
import UserProfileForm, {
  requiredFields as baseRequiredFields,
} from "../components/Common/UserProfileForm";

// Add defensive styles to prevent extension conflicts
const formStyles = {
  "& .MuiTextField-root, & .MuiFormControl-root": {
    // Prevent certain extension interactions
    "& input, & textarea": {
      // Ensure consistent behavior across browsers and extensions
      transition: "none !important",
    },
  },
};

/**
 * @returns {JSX.Element}
 * @component
 */
const SignUp = () => {
  // Required form fields for UI indication and data validation
  const requiredFields = [
    ...baseRequiredFields,
    "password",
    "income", // "Do you live and work in a Low or Middle-Income Country?"
    "title",
    "phone_number",
    "country",
    "address",
    "institution",
    "profession",
    "dob",
  ];

  const formFields = {
    income: "",
    title: "Dr.",
    first_name: "",
    middle_name: "",
    last_name: "",
    email_id: "",
    password: "",
    phone_number: "",
    institution: "",
    address: "",
    profession: "",
    profession_other: "",
    specialty: [],
    specialty_other: "",
    subspecialty: [],
    subspecialty_other: "",
    race: "",
    ethnicity: "",
    gender: "",
    pronouns: "",
    sexual_orientation: "",
    dob: dayjs().subtract(20, "year").month(0).date(1), // January 1st, 20 years ago
    language: [],
    language_other: "",
    directory_show: 1, // Default to checked (1)
    // Metadata
    ip_address: "",
    plan_type: "",
  };
  const isLoading = useSelector((state) => state.pageContents?.loading); // Loading state from Redux
  const applicationFormRef = useRef(null); // This ref is for the container, for scrolling
  const actualFormRef = useRef(null); // New ref for the actual <form> element
  const navigate = useNavigate();
  const [ipAddress, setIpAddress] = useState("");
  const [formData, setFormData] = useState(formFields);
  const [formErrors, setFormErrors] = useState(
    updateNullOfObjectValues(formFields),
  );
  const [submitting, setSubmitProgress] = useState(false);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [success, setSuccess] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [lmicWarning, setLmicWarning] = useState(null);

  // Helper function to scroll to top and show errors
  const scrollToError = () => {
    if (applicationFormRef.current) {
      applicationFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Get IP address on component mount
  useEffect(() => {
    const _getIpAddress = async () => setIpAddress(await getIpAddress());

    _getIpAddress();

    const params = new URLSearchParams(window.location.search);
    setFormData({
      ...formData,
      email_id: params.get("email"),
    });

    // Add global event listener to prevent extension interference
    const handleGlobalKeyDown = (e) => {
      // Check if the event target is within our form
      if (actualFormRef.current && actualFormRef.current.contains(e.target)) {
        // Prevent extensions from interfering with form interactions
        if (e && typeof e.stopPropagation === "function") {
          e.stopPropagation();
        }
      }
    };

    // Add listeners for common extension trigger events
    document.addEventListener("keydown", handleGlobalKeyDown, {
      capture: true,
    });

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown, {
        capture: true,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCountryChange = (value) => {
    setFormData((prev) => ({ ...prev, country: value }));

    // Auto-update LMIC selection based on country
    if (value) {
      const suggested = getSuggestedLMICSelection(value);
      if (suggested) {
        // Always update LMIC selection when country changes
        setFormData((prev) => ({ ...prev, income: suggested }));

        // Clear any previous warning since we're auto-updating
        setLmicWarning(null);
      }
    } else {
      // Clear LMIC selection if no country selected
      setFormData((prev) => ({ ...prev, income: "" }));
      setLmicWarning(null);
    }
  };

  const handleLanguageChange = (values) => {
    // Ensure values is treated as array
    const languagesArray = Array.isArray(values)
      ? values
      : [values].filter(Boolean);
    setFormData((prev) => ({ ...prev, language: languagesArray }));

    // Clear language field error when user makes a selection
    setFormErrors({ ...formErrors, language: null });
  };

  const handleChange = (event) => {
    // Prevent browser extensions (like iCloud Passwords) from interfering
    if (event && typeof event.stopPropagation === "function") {
      event.stopPropagation();
    }

    let value = event.target.value;
    if (event.target.type === "checkbox") {
      if (event.target.name === "subspecialty") {
        if (event.target.checked) {
          if (event.target.value === "None") {
            value = ["None"];
          } else {
            let subspecialty = formData.subspecialty;
            subspecialty.push(event.target.value);
            subspecialty = subspecialty.filter((item) => item !== "None");
            value = subspecialty;
          }
        } else {
          value = formData.subspecialty.filter(
            (item) => item !== event.target.value,
          );
        }
      } else {
        if (event.target.checked) {
          value = [...formData[event.target.name], event.target.value];
        } else {
          value = formData[event.target.name].filter(
            (item) => item !== event.target.value,
          );
        }
      }
    }
    if (event.target.name === "profession") {
      if (event.target.value !== "Other") {
        formData.profession_other = "";
      } else if (event.target.value !== "Physician") {
        formData.specialty = [];
        formData.subspecialty_other = "";
        formData.subspecialty = [];
        formData.subspecialty_other = "";
      }
    } else if (event.target.name === "specialty") {
      if (!value.includes("Other")) {
        formData.specialty_other = "";
      }
    } else if (event.target.name === "subspecialty") {
      if (!value.includes("Other")) {
        formData.subspecialty_other = "";
      }
    } else if (event.target.name === "language") {
      if (
        !value.includes("Additional languages you speak that are not listed")
      ) {
        formData.language_other = "";
      }
    }

    setFormData({
      ...formData,
      [event.target.name]: value,
    });
    setFormErrors({ ...formErrors, [event.target.name]: null });

    // Validate LMIC selection when income field changes
    if (event.target.name === "income" && formData.country) {
      const validation = validateLMICSelection(formData.country, value);
      setLmicWarning(validation.warning);
    }
  };

  const handleDOB = (vl) => {
    setFormData({ ...formData, dob: vl });
    setFormErrors({ ...formErrors, dob: null });
  };

  const handlePhoneChange = (value) => {
    setFormData({ ...formData, phone_number: value });
    setFormErrors({ ...formErrors, phone_number: null });
  };

  const handleSubmit = ({ plan_type, amount }) => {
    setError(null);

    try {
      if (!formValidate()) {
        scrollToError();
        return;
      }
    } catch (validationError) {
      console.error(
        "Form validation failed due to extension interference:",
        validationError,
      );
      setError(
        "Form validation failed. Please try disabling browser extensions and try again.",
      );
      scrollToError();
      return;
    }

    setSubmitProgress(true);
    let formdata = {
      ...formData,
      ip_address: ipAddress,
      plan_type,
      payment_amount: amount,
    };

    // Coerce specialty, subspecialty, and language to arrays if they are not already.
    // This ensures that array methods like .includes(), .filter(), .push(), and .join() can be safely called.

    // Specialty
    if (typeof formdata.specialty === "string") {
      // Convert non-empty string to a single-element array, empty string to empty array.
      formdata.specialty = formdata.specialty ? [formdata.specialty] : [];
    } else if (!Array.isArray(formdata.specialty)) {
      formdata.specialty = []; // Default to empty array if not a string or array (e.g., null/undefined)
    }

    // Subspecialty
    if (typeof formdata.subspecialty === "string") {
      formdata.subspecialty = formdata.subspecialty
        ? [formdata.subspecialty]
        : [];
    } else if (!Array.isArray(formdata.subspecialty)) {
      formdata.subspecialty = [];
    }

    // Language (handleLanguageChange should ensure it's an array, but this is a defensive check)
    if (typeof formdata.language === "string") {
      formdata.language = formdata.language ? [formdata.language] : [];
    } else if (!Array.isArray(formdata.language)) {
      formdata.language = [];
    }

    if (formdata.profession === "other") {
      formdata.profession = formdata.profession_other;
    }
    if (formdata.specialty.includes("Other")) {
      formdata.specialty = formdata.specialty.filter(
        (item) => item !== "Other",
      );
      // Only add specialty_other if it has a non-empty, non-whitespace value
      if (formdata.specialty_other && formdata.specialty_other.trim() !== "") {
        formdata.specialty.push(formdata.specialty_other.trim());
      }
    }
    if (formdata.subspecialty.includes("Other")) {
      formdata.subspecialty = formdata.subspecialty.filter(
        (item) => item !== "Other",
      );
      // Only add subspecialty_other if it has a non-empty, non-whitespace value
      if (
        formdata.subspecialty_other &&
        formdata.subspecialty_other.trim() !== ""
      ) {
        formdata.subspecialty.push(formdata.subspecialty_other.trim());
      }
    }

    delete formdata.profession_other;
    delete formdata.specialty_other;
    delete formdata.subspecialty_other;
    delete formdata.language_other;

    // Fix date format: convert from ISO string to MySQL expected format (YYYY-MM-DD)
    if (formdata.dob) {
      // Check if dob is a dayjs object
      if (formdata.dob && isFunction(formdata.dob.format)) {
        formdata.dob = formdata.dob.format("YYYY-MM-DD");
      } else if (isString(formdata.dob)) {
        // Handle if it's already a string (maybe ISO format)
        // Extract just the date part if it's in ISO format
        const dateMatch = formdata.dob.match(/^(\d{4}-\d{2}-\d{2})/);
        if (dateMatch) {
          formdata.dob = dateMatch[1];
        }
      }
    }

    // Join arrays into comma-separated strings. Filter out any falsey values (e.g., empty strings) first.
    formdata.language = formdata.language.filter(Boolean).join(", ");
    formdata.specialty = formdata.specialty.filter(Boolean).join(", ");
    formdata.subspecialty = formdata.subspecialty.filter(Boolean).join(", ");

    onlineApplicationSubmit(formdata)
      .then((res) => {
        if (res.data.status) {
          // Store the application ID returned from the server
          const applicationId = res.data.application_id;
          setApplicationId(applicationId);
          // Signed checkout token proves ownership for the public payment routes
          const checkoutToken = res.data.checkout_token || null;
          storeCheckoutToken(applicationId, checkoutToken);

          if (amount > 0) {
            // For paid plans, proceed with payment flow
            loadClientSecret({
              application_id: applicationId,
              amount,
              plan_type,
              membership_term_key:
                res.data.membership_checkout?.membership_term_key || null,
              checkout_token: checkoutToken,
            });
          } else {
            // For free plans, create complimentary payment record directly
            const freePaymentPayload = {
              amount: 0,
              user_id: null,
              plan_type,
              linked_table: "online_application",
              linked_id: applicationId,
              checkout_token: checkoutToken,
            };
            // Zero-dollar signup has no Stripe intent that needs to be tied to
            // the quoted term. Let the server resolve and snapshot the current
            // target at completion so a form left open across a term boundary
            // cannot submit a stale browser-held membership_term_key.
            createPaymentIntent(freePaymentPayload)
              .then(() => {
                // Free plans directly go to success page
                setSubmitProgress(false);
                setFormData(formFields);
                navigate(
                  `/payment-complete${objectToQueryString({
                    payment_type: "online_application",
                    application_id: applicationId,
                    free_plan: true,
                  })}`,
                );
              })
              .catch((err) => {
                console.error("Error recording free plan payment:", err);
                // A zero-dollar membership still needs a succeeded local payment
                // record so activation and entitlement processing can complete.
                // Do not show a successful enrollment when that record failed.
                setSubmitProgress(false);
                setError(
                  err?.response?.data?.message ||
                    "Your application was saved, but we could not complete your complimentary membership. Please contact support.",
                );
                scrollToError();
              });
          }
        } else {
          setError(res.data.message);
          setSubmitProgress(false);
          scrollToError();
        }
      })
      .catch((err) => {
        console.error("Error submitting application:", err);
        setSubmitProgress(false);
        setError("Something went wrong. Please try again later.");
        scrollToError();
      });
  };

  const formValidate = () => {
    try {
      let newErrors = {}; // Will store errors for the current validation pass
      let isValid = true;
      // Use a copy of the current global formErrors, which might contain specific errors
      // set by UserProfileForm (e.g., password mismatch).
      const currentGlobalErrors = { ...formErrors };

      requiredFields.forEach((field) => {
        const isFieldEmpty =
          has(formData, [field]) &&
          ((isString(formData[field]) && size(trim(formData[field])) === 0) ||
            size(formData[field]) === 0);

        if (currentGlobalErrors[field]) {
          // If a specific error for this field already exists (e.g., from UserProfileForm)
          newErrors[field] = currentGlobalErrors[field];
          isValid = false; // Field is invalid due to existing specific error
        } else if (isFieldEmpty) {
          // If no specific error and the field is empty, then it's a "required" error
          newErrors[field] = "This field is required";
          isValid = false;
        }
      });

      // Email validation (ensure it respects existing specific errors if any)
      if (!formData.email_id) {
        if (!newErrors.email_id) {
          // Set "required" only if no specific error already exists
          newErrors.email_id = "This field is required";
        }
        isValid = false;
      } else if (!isEmail(formData.email_id)) {
        if (!newErrors.email_id) {
          // Set "invalid" only if no specific error already exists
          newErrors.email_id = "Invalid email address";
        }
        isValid = false;
      }

      // Phone validation - only if a value exists (not strictly required for form validity unless specified)
      if (formData.phone_number && !isValidPhoneNumber(formData.phone_number)) {
        if (!newErrors.phone_number) {
          // Set "invalid" only if no specific error already exists
          newErrors.phone_number = "Phone number is invalid";
        }
        // Note: Original code didn't set isValid = false here.
        // If an invalid optional phone number should block submission, uncomment:
        // isValid = false;
      }

      setFormErrors(newErrors); // Update the form errors state
      return isValid;
    } catch (error) {
      // Handle any errors that might be caused by browser extensions
      console.warn(
        "Form validation encountered an error, possibly due to browser extension interference:",
        error,
      );

      // Return false to prevent form submission if validation fails due to extension interference
      setError(
        "Form validation failed. Please try disabling browser extensions and try again.",
      );
      return false;
    }
  };

  const handlePayNow = ({ event, amount, plan_type }) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    setPaymentAmount(amount);
    handleSubmit({ plan_type, amount });
  };

  const loadClientSecret = ({
    application_id,
    amount,
    plan_type,
    membership_term_key = null,
    checkout_token = null,
  }) => {
    // Double check amount is greater than zero
    if (amount <= 0) {
      log.debug(
        "Attempted to load client secret for free plan, redirecting to success page",
      );
      setSubmitProgress(false);
      navigate(
        `/payment-complete${objectToQueryString({
          payment_type: "online_application",
          application_id: application_id,
          payment_intent: generateRandomString(5),
          free_plan: true,
        })}`,
      );
      return;
    }

    const paymentDetails = {
      amount,
      user_id: null, // Explicitly null for signup
      plan_type: plan_type,
      linked_table: "online_application",
      linked_id: application_id,
      email: formData.email_id, // Pass email for Stripe intent
      checkout_token: checkout_token,
    };
    if (membership_term_key) {
      paymentDetails.membership_term_key = membership_term_key;
    }

    // Use the utility function from StripePayment.jsx
    processStripePaymentIntent({
      createPaymentIntentAction: () => createPaymentIntent(paymentDetails),
      paymentDetails,
      setLoading: setSubmitProgress,
      onSuccess: ({ clientSecret, free_plan }) => {
        // If the server indicates this is a free plan, skip payment dialog
        if (free_plan) {
          setSubmitProgress(false);
          navigate(
            `/payment-complete${objectToQueryString({
              payment_type: "online_application",
              application_id: application_id,
              free_plan: true,
            })}`,
          );
        } else {
          setClientSecret(clientSecret);
          setPaymentDialog(true);
        }
      },
      onError: (errorMessage) => {
        setError(errorMessage || "Failed to initialize payment.");
        scrollToError();
      },
    });
  };

  const handlePaymentDialogClose = (event, reason) => {
    if (isPaymentProcessing) {
      return;
    }
    setPaymentDialog(false);
  };

  const handlePaymentProcess = (d) => setIsPaymentProcessing(d);

  return (
    <>
      <PageLayout pageName="Membership" checkComingSoon={false}>
        <PageBanner
          dense
          mainTitle="Membership Application"
          pageTitle="Membership"
          bannerImage={aboutimg}
        />

        <Container maxWidth="lg" sx={{ py: 5 }} ref={applicationFormRef}>
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 4,
                "& .MuiAlert-message": {
                  fontSize: "1rem",
                  fontWeight: 500,
                },
              }}
              variant="filled"
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 4 }}>
              {success}
            </Alert>
          )}

          <Paper elevation={2} sx={{ p: 3, mb: 4, ...formStyles }}>
            <form
              ref={actualFormRef}
              onKeyDown={(e) => {
                // Prevent browser extensions from interfering with form-level keyboard events
                if (e && typeof e.stopPropagation === "function") {
                  e.stopPropagation();
                }
              }}
              data-form-type="signup"
              data-signup-form="true"
            >
              <UserProfileForm
                {...{
                  usage: "signup",
                  formData,
                  handleInputChange: handleChange,
                  handleDateChange: handleDOB,
                  handlePhoneChange,
                  handleCountryChange,
                  handleLanguageChange,
                  validationErrors: formErrors,
                  required: requiredFields,
                }}
              />

              <Divider sx={{ my: 3 }} />
            </form>
          </Paper>

          <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: "grey.50" }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 8 }}>
                <FormControl>
                  <FormLabel>
                    Do you live and work in a Low or Middle-Income Country? *
                  </FormLabel>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Low and Middle-Income Countries (LMIC) are classified by the
                    World Bank based on gross national income per capita.{" "}
                    <a
                      href="https://datahelpdesk.worldbank.org/knowledgebase/articles/906519-world-bank-country-and-lending-groups"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "inherit", textDecoration: "underline" }}
                    >
                      View the current World Bank country classifications
                    </a>
                    .
                  </Typography>
                  <RadioGroup
                    row
                    name="income"
                    value={formData.income}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="Yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="No"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                  {formErrors.income && (
                    <FormHelperText error>{formErrors.income}</FormHelperText>
                  )}
                  {lmicWarning && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      {lmicWarning}
                    </Alert>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <Divider sx={{ my: 3 }} />

          {/* Payment Options Section */}
          <Box sx={{ my: 4 }}>
            <Typography variant="h5" align="center" gutterBottom>
              Annual Membership Fee
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              {products
                .filter(
                  (p) =>
                    p.category === "membership" &&
                    get(p, "admin_only") !== true,
                )
                .filter((plan) => {
                  // Filter based on Low/Middle-Income Country selection
                  if (formData.income === "Yes") {
                    // Show only international and trainee memberships for LMIC
                    return [
                      "membership_international",
                      "membership_trainee",
                    ].includes(plan.plan_type);
                  } else if (formData.income === "No") {
                    // Hide international membership for non-LMIC countries
                    return plan.plan_type !== "membership_international";
                  }
                  // Show all options if no selection made yet
                  return true;
                })
                .map((plan) => (
                  <Grid key={plan.plan_type} size={{ xs: 12, md: 3, sm: 6 }}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderTop: `5px solid ${plan.borderColor}`,
                      }}
                    >
                      <Typography variant="h6" gutterBottom align="center">
                        {plan.name}
                      </Typography>
                      <Typography variant="body2" align="center">
                        {plan.description}
                      </Typography>
                      <Box sx={{ textAlign: "center", my: 2 }}>
                        <Typography variant="h4" color="primary">
                          ${plan.price}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: "auto", pt: 2, textAlign: "center" }}>
                        <Button
                          variant="contained"
                          onClick={(event) =>
                            handlePayNow({
                              event,
                              amount: plan.price,
                              plan_type: plan.plan_type,
                            })
                          }
                          disabled={submitting}
                          endIcon={<ArrowForwardIcon />}
                          fullWidth
                          color={plan.color}
                        >
                          {plan.buttonText || "Pay Now"}
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          </Box>
        </Container>
      </PageLayout>

      {/* Replace the Dialog with StripePaymentModal */}
      <StripePaymentModal
        open={paymentDialog}
        onClose={handlePaymentDialogClose}
        clientSecret={clientSecret}
        amount={paymentAmount}
        onPaymentProcess={handlePaymentProcess}
        successParams={{
          payment_type: "online_application",
          application_id: applicationId,
        }}
        dialogTitle="Complete Your Membership Payment"
      />

      {/* Loading overlay for payment intent creation */}
      <PaymentIntentLoader
        show={submitting}
        title="Processing your application..."
      />

      {isLoading ? <Preloader /> : null}
    </>
  );
};

export default SignUp;
